import {
  ASC_PORTAL_MICRO_APP,
  ASC_TIER_1_SUPPORT_ROLE,
  ASC_PATH_PREFIX,
  MGMT_OVERVIEW_ROUTE,
  DASHBOARDS_MICRO_APP,
  DASHBOARDS_PATH_PREFIX,
  SAAS_MICRO_APP_NAME,
  DLP_MICRO_APP_NAME,
  DLP_APP_ID,
  NG_CASB_APP_ID,
  FAWKES_APP_ID,
  SDWAN_APP_ID,
  SCM_APP_ID,
  AIOPS_APP_ID,
  SAAS_APP_ID,
  MSP_MICRO_APP_NAME,
  MSP_PATH_PREFIX,
  RUNNING,
  COMMON_SERVICES_MICRO_APP_NAME,
  PAI_MICRO_APP_NAME,
  HOME_PATH,
  SCM_MERGE_APP_IDS,
  PA_PANORAMA_APP_ID,
  FAWKES_MICRO_APP_NAME,
  PA_DEFAULT_ROUTE,
  DLP_DEFAULT_ROUTE,
  AIOPS_FREE_APP_ID,
  PANORAMA,
} from '../../framework/constants'
import { getAuthState, getFrameworkVars, getState, hasMicroApp, isAppCanBeLaunched } from '../../framework/states'
import { hasTsgSupport } from '../../framework/tsg'
import { isPanoramaManagedTenant, isMigrationStateEligibleForFawkesUI } from './fawkesUtils'
import { ffUseNav3, getShellOverrideConfig } from './navUtils'
import { isUserRoleAdemSupport, isUserRoleHasManagePermission, isUserRoleSaaSAdmin, isUserRoleDlpOrDataSecurityAdmin } from './StartupManager'
import type { ReadonlyTsgInstances } from '../../framework/indexedArrays'

export const getFawkesDefaultRedirect = (): string => {
  const { auth, main } = getState()
  if (hasTsgSupport() && hasMicroApp(ASC_PORTAL_MICRO_APP) && auth?.access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)) {
    return ASC_PATH_PREFIX // only have tier 1 role (ignore browser role), redirect to ASC
  }
  if (ffUseNav3()) {
    return MGMT_OVERVIEW_ROUTE
  }
  const isPanorama = main?.tenant?.type === PANORAMA || isPanoramaManagedTenant(auth)
  const hasDashboards = hasMicroApp(DASHBOARDS_MICRO_APP) // for pa cn still use old insights/dem
  let redirect = MGMT_OVERVIEW_ROUTE
  if ((isPanorama && isUserRoleAdemSupport()) || auth?.access?.hasRole('adem_tier_1_support')) {
    redirect = hasDashboards ? `${DASHBOARDS_PATH_PREFIX}/application-experience` : '/dem/dem-summary'
  }
  else if ((isPanorama && !isMigrationStateEligibleForFawkesUI()) || isUserRoleHasManagePermission() === false) {
    redirect = hasDashboards ? DASHBOARDS_PATH_PREFIX : '/insights/sase-summary'
  }
  else if (isUserRoleSaaSAdmin() && hasMicroApp(SAAS_MICRO_APP_NAME)) {
    redirect = '/manage/saas-security'
  }
  else if (hasMicroApp(DLP_MICRO_APP_NAME) && isUserRoleDlpOrDataSecurityAdmin()) {
    redirect = '/manage/prisma-access/data-loss-prevention'
  }
  return redirect
}

const dlpStandaloneTenant = (instances?: ReadonlyTsgInstances) => {
  return instances?.has(DLP_APP_ID) && !instances.has(NG_CASB_APP_ID) &&
    !instances.has(FAWKES_APP_ID) && !instances.has(SDWAN_APP_ID) &&
    !instances.has(SCM_APP_ID) && !instances.has(AIOPS_APP_ID) && !instances.has(SAAS_APP_ID)
}
export type DefaultRouteOptions = { initRedirection?: boolean }
export const getDefaultRoute = (auth = getAuthState(), { initRedirection }: DefaultRouteOptions = {}): string => { // it won't use getFawkesDefaultRedirect, instead use /prisma-access
  const fxVars = getFrameworkVars()
  const currentRoute = window.location.pathname
  if ((fxVars.aiops_free || fxVars.hub || (fxVars.msp && fxVars.shell_path_overrides)) && fxVars.default_route) {
    return fxVars.default_route
  }
  // 1. ASC with tier 1 role
  if (hasMicroApp(ASC_PORTAL_MICRO_APP) && auth.access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE) &&
    (initRedirection || currentRoute.startsWith(ASC_PATH_PREFIX))) { // avoid redirect to /asc in single tenant view
    return ASC_PATH_PREFIX
  }
  // 2. MSP view
  if (auth.tsg_id && hasMicroApp(MSP_MICRO_APP_NAME) &&
    auth.tsgs?.hasSubTenants(auth.tsg_id) && !auth.instances?.length && // only go to msp if tsg is empty and has sub-tsgs
    ((currentRoute === '/' && initRedirection) || currentRoute.startsWith(MSP_PATH_PREFIX))) { // only redirect for / or in /msp
    // new logic for /msp, if the curernt tsg has parent or children
    return (fxVars.msp_route || MSP_PATH_PREFIX) as string
  }
  // 3. if no running instances in single tenant view
  if (!(auth.instances?.length && auth.instances.some(inst => inst.status === RUNNING)) &&
    hasMicroApp(COMMON_SERVICES_MICRO_APP_NAME)) { // no running instance
    return '/settings/tenants' // APPORTAL-12219: go to tenants to see it is empty or in progress
  }
  // 4. AIOps or ADEM Tier1 (removed, always go to /home)
  // 5. SCM apps goes to /home by default when ft merge_oneapp_tiles and default_route is /home
  if (fxVars.merge_oneapp_tiles && hasMicroApp(PAI_MICRO_APP_NAME) && fxVars.default_route === HOME_PATH &&
    auth.instances && SCM_MERGE_APP_IDS.some(appId => auth.instances.isRunningStatus(appId))) {
    return HOME_PATH
  }
  // 6. Fawkes cloud manage default redirect (not include panorama) /prisma-access
  if (auth.instances?.isFawkesRunning && !auth.instances?.isRunningStatus(PA_PANORAMA_APP_ID) && hasMicroApp(FAWKES_MICRO_APP_NAME)) {
    const fawkesDefaultRoute = fxVars.apps_can_launch?.[FAWKES_APP_ID]?.default_route || PA_DEFAULT_ROUTE
    if (fxVars.default_route === HOME_PATH && fawkesDefaultRoute === PA_DEFAULT_ROUTE) {
      return HOME_PATH
    }
    return fawkesDefaultRoute
  }
  // 7. DLP standalone tenant /data-loss-prevention, no /dashboards
  if (hasMicroApp(DLP_MICRO_APP_NAME) && dlpStandaloneTenant(auth?.instances)) {
    return fxVars.apps_can_launch?.[DLP_APP_ID]?.default_route || DLP_DEFAULT_ROUTE
  }
  // 8. then check for apps_can_launch
  if (fxVars.apps_can_launch && auth.instances) {
    for (const appId of Object.keys(fxVars.apps_can_launch)) {
      if (appId !== FAWKES_APP_ID &&
        auth.instances.isRunningStatus(appId) && isAppCanBeLaunched(auth.instances.get(appId))) {
        const appDefaultRoute = fxVars.apps_can_launch[appId].default_route
        if (appId === AIOPS_FREE_APP_ID && !fxVars.aiops_free) {
          continue
        }
        if (appDefaultRoute) {
          return appDefaultRoute
        }
        else if (appId === SDWAN_APP_ID) {
          return DASHBOARDS_PATH_PREFIX
        }
      }
    }
  }
  // 9. last go to default route
  return fxVars.default_route || HOME_PATH
}

export const getLogoRoute = (): string => {
  const cfg = getShellOverrideConfig()
  if (!cfg) {
    return '/'
  }
  const defaultRoute = cfg['+variables']?.default_route as string || cfg.path || '/'
  return defaultRoute
}
